import confetti from 'canvas-confetti';

/**
 * Celebration utilities for dopamine hits throughout the CV building experience
 */

// Basic confetti burst
export const fireConfetti = () => {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 }
  });
};

// Side cannons for major celebrations
export const fireSideCannons = () => {
  const end = Date.now() + 500;
  const colors = ['#5BA946', '#22c55e', '#4ade80', '#86efac'];

  (function frame() {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: colors
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: colors
    });

    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  }());
};

// Stars burst for achievements
export const fireStars = () => {
  const defaults = {
    spread: 360,
    ticks: 50,
    gravity: 0,
    decay: 0.94,
    startVelocity: 30,
    shapes: ['star'],
    colors: ['#FFD700', '#FFA500', '#FF6347', '#5BA946', '#4169E1']
  };

  confetti({
    ...defaults,
    particleCount: 40,
    scalar: 1.2,
    shapes: ['star']
  });

  confetti({
    ...defaults,
    particleCount: 10,
    scalar: 0.75,
    shapes: ['circle']
  });
};

// Milestone celebrations based on percentage
export const celebrateMilestone = (percentage) => {
  if (percentage >= 100) {
    // Grand finale
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      confetti({
        ...defaults,
        particleCount,
        origin: { x: Math.random() * 0.2 + 0.1, y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: Math.random() * 0.2 + 0.7, y: Math.random() - 0.2 }
      });
    }, 250);
  } else if (percentage >= 75) {
    fireSideCannons();
    fireStars();
  } else if (percentage >= 50) {
    fireSideCannons();
  } else if (percentage >= 25) {
    fireConfetti();
  }
};

// Badge unlock celebration
export const celebrateBadge = () => {
  fireStars();
  
  // Play sound if enabled
  playSound('achievement');
};

// Section complete celebration
export const celebrateSection = () => {
  confetti({
    particleCount: 50,
    spread: 60,
    origin: { y: 0.7 },
    colors: ['#5BA946', '#22c55e', '#4ade80']
  });
  
  playSound('success');
};

// Encouragement messages based on progress
export const getEncouragementMessage = (percentage, language = 'fr') => {
  const messages = {
    fr: {
      0: ["C'est parti ! 🚀", "Commençons votre CV !", "Première étape vers le succès !"],
      25: ["Excellent début ! 💪", "Vous êtes sur la bonne voie !", "Continuez comme ça !"],
      50: ["Mi-chemin atteint ! 🎯", "Impressionnant ! La moitié est faite !", "Vous y êtes presque !"],
      75: ["Presque terminé ! ⭐", "Plus que quelques détails !", "Le finish est en vue !"],
      90: ["Dernière ligne droite ! 🏁", "Encore un petit effort !", "Vous touchez au but !"],
      100: ["CV Parfait ! 🎉", "Félicitations ! Votre CV est complet !", "Bravo ! Mission accomplie !"]
    },
    en: {
      0: ["Let's go! 🚀", "Let's start your CV!", "First step to success!"],
      25: ["Great start! 💪", "You're on the right track!", "Keep it up!"],
      50: ["Halfway there! 🎯", "Impressive! Half done!", "You're almost there!"],
      75: ["Almost done! ⭐", "Just a few more details!", "The finish line is in sight!"],
      90: ["Final stretch! 🏁", "One more push!", "You're so close!"],
      100: ["Perfect CV! 🎉", "Congratulations! Your CV is complete!", "Bravo! Mission accomplished!"]
    }
  };

  const lang = messages[language] || messages.fr;
  let level = 0;
  if (percentage >= 100) level = 100;
  else if (percentage >= 90) level = 90;
  else if (percentage >= 75) level = 75;
  else if (percentage >= 50) level = 50;
  else if (percentage >= 25) level = 25;

  const levelMessages = lang[level];
  return levelMessages[Math.floor(Math.random() * levelMessages.length)];
};

// Random tips
export const getRandomTip = (language = 'fr') => {
  const tips = {
    fr: [
      "💡 Astuce : Utilisez des verbes d'action pour décrire vos expériences !",
      "💡 Saviez-vous ? Les recruteurs passent en moyenne 7 secondes sur un CV !",
      "💡 Conseil : Quantifiez vos réalisations avec des chiffres quand c'est possible.",
      "💡 Pro tip : Adaptez votre CV à chaque offre d'emploi.",
      "💡 Astuce : Les soft skills sont aussi importantes que les hard skills !",
      "💡 Saviez-vous ? Un CV bien structuré augmente vos chances de 40% !",
      "💡 Conseil : Relisez toujours votre CV avant de l'envoyer.",
      "💡 Pro tip : Utilisez des mots-clés du secteur visé."
    ],
    en: [
      "💡 Tip: Use action verbs to describe your experiences!",
      "💡 Did you know? Recruiters spend an average of 7 seconds on a CV!",
      "💡 Advice: Quantify your achievements with numbers when possible.",
      "💡 Pro tip: Tailor your CV to each job offer.",
      "💡 Tip: Soft skills are as important as hard skills!",
      "💡 Did you know? A well-structured CV increases your chances by 40%!",
      "💡 Advice: Always proofread your CV before sending.",
      "💡 Pro tip: Use keywords from the targeted sector."
    ]
  };

  const langTips = tips[language] || tips.fr;
  return langTips[Math.floor(Math.random() * langTips.length)];
};

// Sound effects (optional, with user preference check)
const sounds = {
  success: '/sounds/success.mp3',
  achievement: '/sounds/achievement.mp3',
  levelUp: '/sounds/levelup.mp3',
  click: '/sounds/click.mp3'
};

export const playSound = (type) => {
  // Check if sounds are enabled in localStorage
  const soundsEnabled = localStorage.getItem('soundsEnabled') !== 'false';
  if (!soundsEnabled) return;

  // For now, we'll skip actual sound playing as we don't have the audio files
  // But the infrastructure is ready
  console.log(`[Sound] Would play: ${type}`);
};

// Toggle sounds
export const toggleSounds = () => {
  const current = localStorage.getItem('soundsEnabled') !== 'false';
  localStorage.setItem('soundsEnabled', !current);
  return !current;
};

export const areSoundsEnabled = () => {
  return localStorage.getItem('soundsEnabled') !== 'false';
};
